import * as React from 'react'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { GridEventListener } from '../../../models/events/gridEventListener'
import { GridApiCommon } from '../../../models/api/gridApiCommon'
import style from '../../../style'

export const useGridColumnPinning = (
  apiRef: React.MutableRefObject<GridApiCommon>
): void => {
  const [enabled, setIsEnabled] = React.useState(false)
  const { columnPinning } = apiRef.current.tableInstance.getState()

  React.useEffect(() => {
    const isPinned = apiRef.current.tableInstance.getIsSomeColumnsPinned()
    if (isPinned) {
      setIsEnabled(true)
    } else {
      setIsEnabled(false)
    }
  }, [apiRef, columnPinning])

  // Each visible row (not to be confused with a filter result) is composed of a central .MuiDataGrid-row element
  // and up to two additional .MuiDataGrid-row's, one for the columns pinned to the left and another
  // for those on the right side. When hovering any of these elements, the :hover styles are applied only to
  // the row element that was actually hovered, not its additional siblings. To make it look like a contiguous row,
  // this method adds/removes the .Mui-hovered class to all of the row elements inside one visible row.
  const updateHoveredClassOnSiblingRows = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!enabled) {
        return
      }

      const index = event.currentTarget.dataset.rowindex
      const rowElements =
        apiRef.current.virtualScrollerRef!.current!.querySelectorAll(
          `.${style.row}[data-rowindex="${index}"]`
        )
      rowElements.forEach(row => {
        // Ignore rows from other grid inside the hovered row
        if (
          row.closest(`.${style.virtualScroller}`) ===
          apiRef.current.virtualScrollerRef!.current!
        ) {
          if (event.type === 'mouseenter') {
            row.classList.add(style['row--hovered'])
            // @ts-ignore
            row.dataset.rowHover = true
          } else {
            row.classList.remove(style['row--hovered'])
            // @ts-ignore
            delete row.dataset.rowHover
          }
        }
      })
    },
    [apiRef, enabled]
  )

  const handleMouseEnter = React.useCallback<
    GridEventListener<'rowMouseEnter'>
  >(
    (params, event) => {
      updateHoveredClassOnSiblingRows(event)
    },
    [updateHoveredClassOnSiblingRows]
  )

  const handleMouseLeave = React.useCallback<
    GridEventListener<'rowMouseLeave'>
  >(
    (params, event) => {
      updateHoveredClassOnSiblingRows(event)
    },
    [updateHoveredClassOnSiblingRows]
  )

  useGridApiEventHandler(apiRef, 'rowMouseEnter', handleMouseEnter)
  useGridApiEventHandler(apiRef, 'rowMouseLeave', handleMouseLeave)
}
